import { supabase } from "@/infrastructure/supabase/client";
import type { WeightsPresetRow } from "@/infrastructure/supabase/database.types";

export interface CreateWeightsPresetInput {
  slug: string;
  name: string;
  description: string | null;
  weights: Record<string, number>;
}

export interface UpdateWeightsPresetPatch {
  name?: string;
  description?: string | null;
  weights?: Record<string, number>;
}

export const weightsPresetsRepository = {
  async list(): Promise<WeightsPresetRow[]> {
    const { data, error } = await supabase
      .from("weights_presets")
      .select("*")
      .order("name", { ascending: true });
    if (error) throw error;
    return data ?? [];
  },

  async getDefault(): Promise<WeightsPresetRow | null> {
    const { data, error } = await supabase
      .from("weights_presets")
      .select("*")
      .eq("is_default", true)
      .maybeSingle();
    if (error) throw error;
    return data;
  },

  async create(input: CreateWeightsPresetInput): Promise<WeightsPresetRow> {
    const { data, error } = await supabase
      .from("weights_presets")
      .insert({ ...input, is_default: false })
      .select("*")
      .single();
    if (error) throw error;
    return data;
  },

  async update(id: string, patch: UpdateWeightsPresetPatch): Promise<WeightsPresetRow> {
    const { data, error } = await supabase
      .from("weights_presets")
      .update(patch)
      .eq("id", id)
      .select("*")
      .single();
    if (error) throw error;
    return data;
  },

  /**
   * Switch the active preset. The partial unique index allows at most one
   * is_default=true row, so we must clear the current default before setting
   * the new one (clear → set, never two true at once).
   */
  async setDefault(id: string): Promise<void> {
    const { error: clearErr } = await supabase
      .from("weights_presets")
      .update({ is_default: false })
      .eq("is_default", true);
    if (clearErr) throw clearErr;
    const { error: setErr } = await supabase
      .from("weights_presets")
      .update({ is_default: true })
      .eq("id", id);
    if (setErr) throw setErr;
  },

  async duplicate(id: string): Promise<WeightsPresetRow> {
    const { data: src, error } = await supabase
      .from("weights_presets")
      .select("*")
      .eq("id", id)
      .single();
    if (error) throw error;
    const suffix = Math.random().toString(36).slice(2, 7);
    const { data, error: insErr } = await supabase
      .from("weights_presets")
      .insert({
        slug: `${src.slug}-copy-${suffix}`,
        name: `${src.name} (copy)`,
        description: src.description,
        weights: src.weights,
        is_default: false,
      })
      .select("*")
      .single();
    if (insErr) throw insErr;
    return data;
  },

  async remove(id: string): Promise<void> {
    const { error } = await supabase.from("weights_presets").delete().eq("id", id);
    if (error) throw error;
  },
};
